/**
 * @spec docs/specs/features/auto-whitelist-privy-wallets.md
 *
 * API client for bridge relayer self-registration.
 * Routes through the swap-api proxy (POST /bridge/register) so the frontend
 * does not need a direct URL to the bridge-relayer.
 */
import { env } from './env';

const SWAP_API_URL = env.swapApiUrl;

export interface RegisterForBridgeResponse {
  address: string;
  registered: boolean;
  reason?: string;
}

/**
 * Register an address for bridge access via Privy-authenticated self-registration.
 *
 * @param address - Wallet address to register
 * @param timestamp - Unix timestamp (seconds) signed in the EIP-712 message
 * @param signature - EIP-712 signature of RegisterForBridge
 * @param accessToken - Privy JWT access token
 */
export async function registerForBridge(
  address: string,
  timestamp: number,
  signature: string,
  accessToken: string
): Promise<RegisterForBridgeResponse> {
  if (!SWAP_API_URL) {
    throw new Error('Swap API URL not configured');
  }

  const response = await fetch(`${SWAP_API_URL}/bridge/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ address, timestamp, signature }),
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => ({ error: 'Unknown error' }))) as {
      error?: string;
    };
    throw new Error(data.error || `Registration failed (${response.status})`);
  }

  return response.json() as Promise<RegisterForBridgeResponse>;
}
